import {
	Component,
	DestroyRef,
	ElementRef,
	OnDestroy,
	ViewChild,
	effect,
	inject,
	input,
	output,
	signal,
} from '@angular/core';
import { Editor } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';

export function serializeHtmlDocument(doc: Document): string {
	const dt = doc.doctype;
	const doctype = dt
		? `<!DOCTYPE ${dt.name}${dt.publicId ? ` PUBLIC "${dt.publicId}"` : ''}${dt.systemId ? ` "${dt.systemId}"` : ''}>`
		: '<!DOCTYPE html>';
	const htmlElement = doc.documentElement.cloneNode(true) as HTMLElement;
	htmlElement.querySelectorAll('style[data-lvr-editor-style]').forEach((style) => style.remove());
	return `${doctype}\n${htmlElement.outerHTML}`;
}

@Component({
	selector: 'lvr-html-editor',
	template: `
		<div class="wrap">
			<iframe #frame class="doc-frame" referrerpolicy="no-referrer"></iframe>
		</div>
	`,
	styles: [
		`
			.wrap {
				border: 1px solid #e2e8f0;
				border-radius: 8px;
				overflow: hidden;
			}
			.doc-frame {
				width: 100%;
				height: 70vh;
				border: 0;
				background: #fff;
			}
		`,
	],
})
export class LvrHtmlEditor implements OnDestroy {
	private readonly destroyRef = inject(DestroyRef);
	public readonly html = input.required<string>();
	public readonly readOnly = input<boolean>(false);
	public readonly updateDebounce = input<number>(200);
	public readonly dataTypes = input<string[]>(['[data-type="user-interactive"]', '[data-type="user-interactive-fds"]']);
	public readonly docChange = output<string>();

	@ViewChild('frame', { static: true }) public readonly frameRef!: ElementRef<HTMLIFrameElement>;
	public readonly ready = signal(false);

	private editors = new Map<HTMLElement, Editor>();
	private teardown: (() => void)[] = [];
	private lastApplied = '';
	private lastEmitted = '';
	private emitRaf?: number;
	private emitTimeout?: number;
	private iframeView?: Window;

	public constructor() {
		effect(() => {
			this.loadHtml(this.html(), this.readOnly());
		});
		this.destroyRef.onDestroy(() => this.ngOnDestroy());
	}

	public ngOnDestroy() {
		this.teardown.forEach((fn) => fn());
		this.teardown = [];
		for (const ed of this.editors.values()) ed.destroy();
		this.editors.clear();
		this.lastEmitted = '';
		const view = this.iframeView ?? window;
		if (this.emitRaf !== undefined) view.cancelAnimationFrame(this.emitRaf);
		if (this.emitTimeout !== undefined) view.clearTimeout(this.emitTimeout);
		this.iframeView = undefined;
		this.ready.set(false);
	}

	/** Load HTML into the iframe and wait until its DOM is fully parsed. */
	private async loadHtml(docHtml: string, readOnly: boolean) {
		if (!docHtml) return;
		// Ignore feedback loop when parent writes back our own emitted content.
		if (this.ready() && docHtml === this.lastEmitted) return;
		// Avoid reloading identical content if already ready.
		if (docHtml === this.lastApplied && this.ready()) return;

		this.lastApplied = docHtml;
		this.ready.set(false);

		// Clean previous resources BEFORE loading new doc.
		this.ngOnDestroy();

		const iframe = this.frameRef.nativeElement;
		const doc = this.writeHtmlIntoIframe(iframe, docHtml);
		if (!doc) return;

		await this.waitForDocumentReady(doc);
		if (this.lastApplied !== docHtml) return;

		if (readOnly) {
			this.applyReadOnly(doc);
			this.ready.set(true);
			return;
		}
		this.initIframeWithDoc(doc);
		this.ready.set(true);
	}

	private writeHtmlIntoIframe(iframe: HTMLIFrameElement, html: string): Document | undefined {
		const targetDoc = iframe.contentDocument ?? iframe.contentWindow?.document;
		if (!targetDoc) return undefined;

		try {
			targetDoc.open();
			// TODO: Needs to be replaced with Trusted Types and setHTML there as soon as they're available in all Browsers (possible end of 2026)
			targetDoc.write(html);
			targetDoc.close();
		} catch {
			return undefined;
		}

		return iframe.contentDocument ?? targetDoc;
	}

	private waitForDocumentReady(doc: Document): Promise<void> {
		return new Promise((resolve) => {
			if (doc.readyState !== 'loading') {
				window.setTimeout(resolve, 0);
				return;
			}

			const onReady = () => {
				if (doc.readyState === 'loading') return;
				doc.removeEventListener('readystatechange', onReady);
				resolve();
			};
			doc.addEventListener('readystatechange', onReady);
		});
	}

	/** Initialize editing, mount TipTap on islands, set observers/guards. */
	private initIframeWithDoc(doc: Document) {
		// Inject minimal editor styles into the iframe.
		const style = doc.createElement('style');
		style.setAttribute('data-lvr-editor-style', '');
		const selector = this.dataTypes().join(',');
		const focusWithinSelector = this.dataTypes()
			.map((s) => `${s}:focus-within`)
			.join(',');
		style.textContent = `
      /* visual hint for editable islands */
      ${selector} { outline: 2px dashed rgba(99,102,241,.5); outline-offset: 2px; min-height: 1rem; }
      ${focusWithinSelector} { outline: 2px solid rgba(99,102,241,.9); }
      /* optional baseline for TipTap content */
      .tiptap { line-height: 1.5; white-space: pre; overflow-x: auto; }
      .tiptap p { margin: .25rem 0; }
      .tiptap p.is-editor-empty:first-child::before { content: attr(data-placeholder); color:#9ca3af; float:left; pointer-events:none; height:0; }
	  .tiptap,
	  .ProseMirror { overflow-wrap: break-word; word-wrap: break-word; white-space: break-spaces; }
    `;
		doc.head?.appendChild(style);

		// Guard: block edits outside islands.
		const stopOutside = (ev: Event) => {
			const t = ev.target as Node | null;
			const inIsland = (t as Element | null)?.closest?.(selector);
			if (!inIsland) {
				ev.preventDefault();
				ev.stopPropagation();
			}
		};
		doc.addEventListener('beforeinput', stopOutside, true);
		this.teardown.push(() => doc.removeEventListener('beforeinput', stopOutside, true));

		const emit = this.createEmitter(doc);

		const islands = Array.from(doc.querySelectorAll<HTMLElement>(selector));
		islands.forEach((el) => this.mountTipTap(el, emit));
	}

	/** Create a TipTap editor mounted directly on the island element. */
	private mountTipTap(hostEl: HTMLElement, emit: () => void) {
		const initialContent = hostEl.innerHTML;

		// …clear the host so we don't render duplicate static content.
		// (use replaceChildren() for correctness across node types)
		hostEl.replaceChildren();

		const editor = new Editor({
			element: hostEl, // hostEl belongs to the iframe DOM; ownerDocument is correct
			content: initialContent,
			editable: true,
			injectCSS: false, // we injected our own CSS into the iframe <head>
			extensions: [
				StarterKit.configure({
					heading: false,
					blockquote: false,
					codeBlock: false,
					// enable/disable more as needed
				}),
			],
			editorProps: {
				attributes: { class: 'tiptap', 'data-placeholder': 'Type…' },
				handlePaste: (_view, _ev) => false,
				handleDrop: (_view, _ev) => false,
			},
			onUpdate: () => emit(),
		});

		this.editors.set(hostEl, editor);
		this.teardown.push(() => {
			editor.destroy();
		});
	}

	/** Build a debounced emitter for the given iframe document. */
	private createEmitter(doc: Document) {
		this.iframeView = doc.defaultView ?? window;
		return () => {
			if (!this.ready()) return;
			const view = this.iframeView ?? window;
			if (this.emitRaf !== undefined) view.cancelAnimationFrame(this.emitRaf);
			if (this.emitTimeout !== undefined) view.clearTimeout(this.emitTimeout);
			this.emitRaf = view.requestAnimationFrame(() => {
				this.emitTimeout = view.setTimeout(() => {
					const html = this.serializeFull(doc);
					if (html === this.lastEmitted) return;
					this.lastEmitted = html;

					this.docChange.emit(html);
					this.emitTimeout = undefined;
					this.emitRaf = undefined;
				}, this.updateDebounce());
			});
		};
	}

	/** Serialize entire iframe document, preserving doctype. */
	private serializeFull(doc: Document): string {
		return serializeHtmlDocument(doc);
	}

	/** Disable persisted editable elements and native controls inside the iframe document. */
	private applyReadOnly(doc: Document) {
		doc.querySelectorAll<HTMLElement>('[contenteditable]').forEach((el) => {
			el.setAttribute('contenteditable', 'false');
			el.removeAttribute('tabindex');
		});

		doc.querySelectorAll<HTMLInputElement>('input').forEach((el) => {
			el.readOnly = true;
		});

		doc.querySelectorAll<HTMLTextAreaElement>('textarea').forEach((el) => {
			el.readOnly = true;
		});

		doc.querySelectorAll<HTMLSelectElement>('select').forEach((el) => {
			el.disabled = true;
		});

		doc.querySelectorAll<HTMLButtonElement>('button').forEach((el) => {
			el.disabled = true;
		});
	}
}
